import { BottomSheetModalMethods } from '@gorhom/bottom-sheet/lib/typescript/types'
import AsyncStorage from '@react-native-async-storage/async-storage'
import { NavigationContainerRefWithCurrent } from '@react-navigation/native'
import React, { RefObject } from 'react'
import { SafeAreaProvider } from 'react-native-safe-area-context'
import { QueryClient, QueryClientProvider } from 'react-query'
import { createAsyncStoragePersistor } from 'react-query/createAsyncStoragePersistor-experimental'
import { createWebStoragePersistor } from 'react-query/createWebStoragePersistor-experimental'
import { ReactQueryDevtools } from 'react-query/devtools'
import { persistQueryClient } from 'react-query/persistQueryClient-experimental'

import { platform } from 'utils/constants'

import { CommentModalProvider } from 'contexts/CommentModalContext'
import { DialogModalProvider } from 'contexts/DialogModalContext'
import { FeatureFlagContextProvider } from 'contexts/FeatureFlagContext'
import { FocusModeContextProvider } from 'contexts/FocusModeContext'
import { InfoModalProvider } from 'contexts/InfoModalContext'
import { LaunchpadModalProvider } from 'contexts/LaunchpadModalContext'
import { TextEditorModalProvider } from 'contexts/TextEditorModalContext'

import { AuthProvider } from './AuthContext'
import { CoachOnboardingModalProvider } from './CoachOnboardingModalContext'
import { CreatePostModalProvider } from './CreatePostModalContext'
import { LoadingProvider } from './LoadingProvider'
import { ThemeProvider } from './ThemeContext'

/*
  This combines all the providers into one context.
*/

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      cacheTime: 1000 * 60 * 60 * 24 * 7, // 24 hours * 7 days
    },
  },
})
let localStoragePersistor

if (platform.IS_WEB) {
  localStoragePersistor = createWebStoragePersistor({
    storage: window.localStorage,
  })
} else {
  localStoragePersistor = createAsyncStoragePersistor({
    storage: AsyncStorage,
  })
}

persistQueryClient({
  queryClient,
  persistor: localStoragePersistor,
})

interface RootContextProps {
  launchpadModalRef: RefObject<BottomSheetModalMethods>
  coachOnboardingModalRef: RefObject<BottomSheetModalMethods>
  createPostModalRef: RefObject<BottomSheetModalMethods>
  commentModalRef: RefObject<BottomSheetModalMethods>
  textEditorModalRef: RefObject<BottomSheetModalMethods>
  infoModalRef: RefObject<BottomSheetModalMethods>
  dialogModalRef: RefObject<BottomSheetModalMethods>
  children: React.ReactNode
  drawerNavigationRef: NavigationContainerRefWithCurrent<ReactNavigation.RootParamList>
}

export const RootContext = ({
  launchpadModalRef,
  coachOnboardingModalRef,
  createPostModalRef,
  commentModalRef,
  textEditorModalRef,
  infoModalRef,
  dialogModalRef,
  children,
  drawerNavigationRef,
}: RootContextProps) => {
  return (
    <SafeAreaProvider>
      <QueryClientProvider client={queryClient}>
        <FeatureFlagContextProvider>
          <FocusModeContextProvider drawerNavigationRef={drawerNavigationRef}>
            <AuthProvider>
              <ThemeProvider>
                <LoadingProvider>
                  <LaunchpadModalProvider launchpadModalRef={launchpadModalRef}>
                    <CoachOnboardingModalProvider coachOnboardingModalRef={coachOnboardingModalRef}>
                      <CreatePostModalProvider createPostModalRef={createPostModalRef}>
                        <CommentModalProvider commentModalRef={commentModalRef}>
                          <TextEditorModalProvider textEditorModalRef={textEditorModalRef}>
                            <InfoModalProvider infoModalRef={infoModalRef}>
                              <DialogModalProvider dialogModalRef={dialogModalRef}>
                                {children}
                              </DialogModalProvider>
                            </InfoModalProvider>
                          </TextEditorModalProvider>
                        </CommentModalProvider>
                      </CreatePostModalProvider>
                    </CoachOnboardingModalProvider>
                  </LaunchpadModalProvider>
                </LoadingProvider>
              </ThemeProvider>
            </AuthProvider>
          </FocusModeContextProvider>
        </FeatureFlagContextProvider>
        {/* RQ Devtools are only available on the web */}
        {platform.IS_WEB && <ReactQueryDevtools initialIsOpen={false} />}
      </QueryClientProvider>
    </SafeAreaProvider>
  )
}
